import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  IconButton,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CloudQueue as CloudQueueIcon,
  FolderOpen as FolderIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import SettingsRowShell from './SettingsRowShell';
import api from '../../services/api';

const GoogleDriveRow = ({ canEdit, showSnackbar }) => {
  const [status, setStatus] = useState({ connected: false, configured: false });
  const [loading, setLoading] = useState(true);
  const [folderInput, setFolderInput] = useState('');
  const [savingFolder, setSavingFolder] = useState(false);

  // Storage backend state
  const [currentBackend, setCurrentBackend] = useState('confluence');
  const [savingBackend, setSavingBackend] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const [driveRes, backendRes] = await Promise.all([
        api.get('/google-drive/status'),
        api.get('/settings/storage-backend'),
      ]);
      setStatus(driveRes.data);
      setFolderInput(driveRes.data.folder_id || '');
      setCurrentBackend(backendRes.data.backend || 'confluence');
    } catch (err) {
      console.error('Failed to load Google Drive status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleConnect = async () => {
    try {
      const response = await api.get('/google-drive/connect');
      const { auth_url } = response.data;
      const w = 600, h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(
        auth_url,
        'google-drive-oauth',
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
      );
      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'GOOGLE_DRIVE_OAUTH_CALLBACK') {
          window.removeEventListener('message', handleMessage);
          if (event.data.success) {
            showSnackbar(`Successfully connected Google Drive as ${event.data.name || event.data.email || 'user'}!`, 'success');
            fetchStatus();
          } else {
            showSnackbar(`Google Drive connection failed: ${event.data.error || 'Unknown error'}`, 'error');
          }
        }
      };
      window.addEventListener('message', handleMessage);
      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
          fetchStatus();
        }
      }, 500);
    } catch (err) {
      showSnackbar('Failed to initiate Google Drive connection: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.delete('/google-drive/disconnect');
      showSnackbar('Google Drive disconnected', 'success');
      fetchStatus();
    } catch (err) {
      showSnackbar('Failed to disconnect Google Drive: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleSaveFolder = async () => {
    if (!folderInput.trim()) {
      showSnackbar('Please enter a Google Drive folder ID', 'warning');
      return;
    }
    try {
      setSavingFolder(true);
      const res = await api.post('/google-drive/configure-folder', { folder_id: folderInput.trim() });
      showSnackbar(`Folder configured: ${res.data.folder_name || res.data.folder_id}`, 'success');
      fetchStatus();
    } catch (err) {
      showSnackbar('Failed to configure folder: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSavingFolder(false);
    }
  };

  const handleBackendChange = async (event) => {
    const newBackend = event.target.value;
    try {
      setSavingBackend(true);
      await api.put('/settings/storage-backend', { backend: newBackend });
      setCurrentBackend(newBackend);
      showSnackbar(`Storage backend switched to ${newBackend === 'google_drive' ? 'Google Drive' : 'Confluence'}`, 'success');
    } catch (err) {
      showSnackbar('Failed to change storage backend: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSavingBackend(false);
    }
  };

  const statusChip = loading
    ? null
    : status.connected
      ? {
          label: status.expired
            ? 'Token Expired'
            : `Connected — ${status.display_name || status.email || 'Google User'}`,
          color: status.expired ? 'warning' : 'success',
        }
      : { label: 'Not Connected', color: 'default' };

  return (
    <SettingsRowShell
      icon={<CloudQueueIcon />}
      title="Google Drive"
      description="Store media files in Google Drive instead of Confluence"
      status={statusChip}
      loading={loading}
      actions={
        <IconButton onClick={fetchStatus} disabled={loading} size="small">
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box>
          {/* ── Connection status ── */}
          {status.connected ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {status.display_name || status.email || 'Google User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {status.email}
                  {status.connected_by && ` · Connected by ${status.connected_by}`}
                  {status.expired && (
                    <Chip label="Expired" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                  )}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect your organization's Google Drive to store media files (screenshots, videos) uploaded in issues.
              This is an org-wide setting — only the super admin needs to connect once.
            </Typography>
          )}

          {/* ── Connect / Disconnect buttons ── */}
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
              {status.connected ? (
                <>
                  <Button variant="outlined" size="small" onClick={handleConnect} startIcon={<RefreshIcon />}>
                    Re-authenticate
                  </Button>
                  <Button variant="outlined" color="error" size="small" onClick={handleDisconnect} startIcon={<LinkOffIcon />}>
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleConnect}
                  startIcon={<LinkIcon />}
                  disabled={!status.configured}
                >
                  Connect Google Drive
                </Button>
              )}
            </Box>
          )}

          {!status.configured && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
              Google Drive OAuth is not configured on the server. Set GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_CLIENT_SECRET.
            </Alert>
          )}

          {/* ── Folder ID configuration ── */}
          {status.connected && canEdit && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FolderIcon fontSize="small" /> Upload Folder
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                All media files will be uploaded to this Google Drive folder. Find the folder ID in the URL when viewing the folder in Google Drive.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. 1AbC_dEfGhIjKlMnOpQrStUv"
                  value={folderInput}
                  onChange={(e) => setFolderInput(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveFolder}
                  disabled={savingFolder}
                  startIcon={savingFolder ? <CircularProgress size={16} /> : <SaveIcon />}
                  sx={{ minWidth: 90, whiteSpace: 'nowrap' }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Storage backend selector ── */}
          {status.connected && canEdit && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Active Storage Backend
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Choose where new media files will be stored. Existing files stored in the previous backend will continue to be accessible.
              </Typography>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Storage Backend</InputLabel>
                <Select
                  value={currentBackend}
                  label="Storage Backend"
                  onChange={handleBackendChange}
                  disabled={savingBackend}
                >
                  <MenuItem value="confluence">Confluence</MenuItem>
                  <MenuItem value="google_drive" disabled={!status.folder_id}>
                    Google Drive
                    {!status.folder_id && ' (configure folder first)'}
                  </MenuItem>
                </Select>
              </FormControl>
              {currentBackend === 'google_drive' && (
                <Alert severity="info" sx={{ mt: 1.5, borderRadius: 1.5 }} icon={false}>
                  New uploads will go to Google Drive. Existing Confluence media will still display correctly.
                </Alert>
              )}
            </Box>
          )}

          {/* Read-only view for non-admins */}
          {!canEdit && status.connected && (
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Storage backend: <strong>{currentBackend === 'google_drive' ? 'Google Drive' : 'Confluence'}</strong>
                {status.folder_id && ` · Folder: ${status.folder_id}`}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </SettingsRowShell>
  );
};

export default GoogleDriveRow;
